import { useEffect, useState } from "react";
import api from "@/api";
import { useAuth } from "@/auth";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Vote, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_LABELS = {
  sin_voluntario: { label: "SIN VOLUNTARIO", className: "bg-red-50 text-red-700 border-red-200" },
  parcial: { label: "COBERTURA PARCIAL", className: "bg-yellow-50 text-yellow-800 border-yellow-200" },
  cubierta: { label: "CUBIERTA", className: "bg-green-50 text-green-700 border-green-200" },
};

export default function CasillasPage() {
  const { subaccount } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(true);

  const [muniFilter, setMuniFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    if (subaccount?.entidad_clave && subaccount.scope === "estatal") {
      api.get("/catalog/municipios", { params: { entidad_clave: subaccount.entidad_clave } })
        .then((r) => setMunicipios(r.data));
    }
    api.get("/casillas/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [subaccount?.entidad_clave, subaccount?.scope]);

  useEffect(() => {
    setLoading(true);
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (muniFilter !== "all") params.municipio_clave = muniFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    if (search.trim()) params.search = search.trim();
    api
      .get("/casillas", { params })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [muniFilter, statusFilter, search, page]);

  const isMunicipal = subaccount?.scope === "municipal";

  return (
    <div className="space-y-6" data-testid="casillas-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="tracking-label text-blue-700">Operación territorial</span>
          <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl text-slate-900">
            Gestión de Casillas
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Catálogo cargado desde EDMSLM (INE) — {subaccount?.entidad_nombre}
            {subaccount?.municipio_nombre ? ` · ${subaccount.municipio_nombre}` : ""}
          </p>
        </div>
        {stats && (
          <div className="text-right">
            <div className="font-display font-black text-3xl text-slate-900 tabular-nums">
              {stats.total.toLocaleString()}
            </div>
            <div className="tracking-label text-slate-500">secciones · catálogo</div>
          </div>
        )}
      </div>

      {/* Status counters */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <StatCounter
            label="Sin voluntario"
            value={stats.sin_voluntario}
            color="red"
            active={statusFilter === "sin_voluntario"}
            onClick={() => setStatusFilter(statusFilter === "sin_voluntario" ? "all" : "sin_voluntario")}
            testid="status-counter-sin"
          />
          <StatCounter
            label="Cobertura parcial"
            value={stats.parcial}
            color="yellow"
            active={statusFilter === "parcial"}
            onClick={() => setStatusFilter(statusFilter === "parcial" ? "all" : "parcial")}
            testid="status-counter-parcial"
          />
          <StatCounter
            label="Cubiertas"
            value={stats.cubierta}
            color="green"
            active={statusFilter === "cubierta"}
            onClick={() => setStatusFilter(statusFilter === "cubierta" ? "all" : "cubierta")}
            testid="status-counter-cubierta"
          />
        </div>
      )}

      {/* Filters */}
      <div className="surface-card p-4 flex flex-col md:flex-row gap-3 md:items-center">
        {!isMunicipal && (
          <Select value={String(muniFilter)} onValueChange={(v) => { setMuniFilter(v); setPage(0); }}>
            <SelectTrigger className="md:max-w-xs" data-testid="casillas-muni-filter">
              <SelectValue placeholder="Todos los municipios" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los municipios</SelectItem>
              {municipios.map((m) => (
                <SelectItem key={m.municipio_clave} value={String(m.municipio_clave)}>
                  {m.municipio_nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="md:max-w-xs" data-testid="casillas-status-filter">
            <SelectValue placeholder="Todos los estatus" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estatus</SelectItem>
            <SelectItem value="sin_voluntario">Sin voluntario</SelectItem>
            <SelectItem value="parcial">Cobertura parcial</SelectItem>
            <SelectItem value="cubierta">Cubierta</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder={isMunicipal ? "Buscar por sección o colonia…" : "Buscar por sección, municipio o localidad…"}
            className="pl-10"
            data-testid="casillas-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="surface-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Vote className="w-10 h-10 mx-auto text-slate-400" />
            <p className="mt-3 text-slate-600">
              No hay casillas con los filtros seleccionados.
            </p>
            {(!subaccount?.catalog_loaded) && (
              <p className="mt-2 text-xs text-amber-700">
                El catálogo aún se está cargando. Espera unos segundos y vuelve a intentar.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th>Estado</Th>
                  <Th>Municipio</Th>
                  {isMunicipal && <Th>Colonia</Th>}
                  <Th>Sección</Th>
                  <Th>Distrito</Th>
                  <Th>Tipo casilla</Th>
                  <Th>Tipo sección</Th>
                  <Th className="text-center">Manzanas</Th>
                  <Th className="text-center">Voluntarios</Th>
                  <Th>Estatus</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr
                    key={`${it.municipio_clave}-${it.seccion}-${i}`}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    data-testid={`casilla-row-${it.seccion}`}
                  >
                    <Td><span className="tabular-nums font-medium">{it.entidad_clave}</span></Td>
                    <Td><span className="font-medium uppercase text-slate-900 tracking-wide">{it.municipio_nombre}</span></Td>
                    {isMunicipal && (
                      <Td><span className="text-slate-700">{(it.localidades || [])[0] || "—"}</span></Td>
                    )}
                    <Td><span className="font-display font-bold tabular-nums">{it.seccion}</span></Td>
                    <Td><span className="tabular-nums">{it.distrito ?? "—"}</span></Td>
                    <Td>{it.tipo_casilla}</Td>
                    <Td><span className="text-slate-600">{it.tipo_seccion || it.tipo_localidad || "—"}</span></Td>
                    <Td className="text-center"><span className="tabular-nums">{it.manzanas_enabled}/{it.manzanas_total}</span></Td>
                    <Td className="text-center">
                      <span className="tabular-nums font-bold">
                        {it.voluntarios}/{it.voluntarios_target}
                      </span>
                    </Td>
                    <Td>
                      <Badge
                        variant="outline"
                        className={`${STATUS_LABELS[it.status].className} text-[10px] uppercase tracking-wider font-bold`}
                        data-testid={`status-badge-${it.status}`}
                      >
                        {STATUS_LABELS[it.status].label}
                      </Badge>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {items.length > 0 && (
          <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600">
            <span>Mostrando {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length}</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(Math.max(0, page - 1))}
                data-testid="casillas-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-2">Página {page + 1}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={items.length < PAGE_SIZE}
                onClick={() => setPage(page + 1)}
                data-testid="casillas-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left tracking-label text-slate-500 font-medium ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}

function StatCounter({ label, value, color, active, onClick, testid }) {
  const colorMap = {
    red: { bar: "before:bg-red-600", text: "text-red-700" },
    yellow: { bar: "before:bg-yellow-500", text: "text-yellow-800" },
    green: { bar: "before:bg-green-600", text: "text-green-700" },
  };
  const c = colorMap[color] || colorMap.red;
  return (
    <button
      onClick={onClick}
      className={`text-left surface-card p-4 relative overflow-hidden before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 ${c.bar} hover:-translate-y-0.5 transition-all duration-200 ${
        active ? "ring-2 ring-slate-900" : ""
      }`}
      data-testid={testid}
    >
      <div className="tracking-label text-slate-500 pl-2">{label}</div>
      <div className={`mt-1 pl-2 font-display font-black text-2xl ${c.text} tabular-nums`}>
        {value.toLocaleString()}
      </div>
    </button>
  );
}
